// HR: employee directory, add employee, and the full employee record.
// Departments exist in the backend but aren't shown or edited here for now.
import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button, Checkbox, FormControlLabel, InputAdornment, MenuItem, TextField } from "@mui/material";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import PersonAddAlt1OutlinedIcon from "@mui/icons-material/PersonAddAlt1Outlined";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import BeachAccessOutlinedIcon from "@mui/icons-material/BeachAccessOutlined";
import TodayOutlinedIcon from "@mui/icons-material/TodayOutlined";
import ChecklistRtlOutlinedIcon from "@mui/icons-material/ChecklistRtlOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import { fileUrl, fullName, fmtDate, inr, thisMonth, EMPLOYMENT_TYPES, ROLE_LABELS, DOC_CATEGORIES } from "./api";
import {
  useLoad,
  useAction,
  Loading,
  Card,
  Pill,
  Tag,
  Avatar,
  Person,
  Empty,
  Segmented,
  ConfirmDialog,
  MonthNav,
  MonthCalendar,
  DayLegend,
} from "./shared";
import { attendanceCells, AttendanceTiles, Balances, GenderField } from "./Me";
import styles from "./Hrms.module.css";

const tf = { size: "small", fullWidth: true };
const btn = { textTransform: "none", fontWeight: 700, fontFamily: "inherit", borderRadius: "10px", boxShadow: "none" };

const JOB_FIELDS = [
  ["first_name", "First name"],
  ["last_name", "Last name"],
  ["work_email", "Work email (their portal login)"],
  ["designation", "Designation"],
  ["joining_date", "Joining date", "date"],
  ["probation_end_date", "Probation ends", "date"],
];
const PERSONAL_FIELDS = [
  ["phone", "Phone"],
  ["personal_email", "Personal email"],
  ["date_of_birth", "Date of birth", "date"],
  ["gender", "Gender", "gender"],
  ["emergency_contact_name", "Emergency contact"],
  ["emergency_contact_phone", "Emergency phone"],
];

function EmployeeForm({ value, onChange, me, employees, selfId }) {
  const set = (k) => (e) => onChange({ ...value, [k]: e.target.value });
  const field = ([k, label, type]) =>
    type === "gender" ? (
      <GenderField key={k} value={value[k]} onChange={set(k)} />
    ) : (
      <TextField key={k} {...tf} type={type || "text"} label={label} InputLabelProps={type ? { shrink: true } : undefined} value={value[k] ?? ""} onChange={set(k)} />
    );
  return (
    <>
      <div className={styles.sectionTitle} style={{ marginTop: 0 }}>
        Job
      </div>
      <div className={styles.formRow}>
        {JOB_FIELDS.map(field)}
        <TextField {...tf} select label="Reporting manager" value={value.manager_id ?? ""} onChange={set("manager_id")}>
          <MenuItem value="">None — HR approves their requests</MenuItem>
          {employees
            .filter((e) => e.id !== selfId)
            .map((e) => (
              <MenuItem key={e.id} value={e.id}>
                {fullName(e)}
              </MenuItem>
            ))}
        </TextField>
        <TextField {...tf} select label="Employment type" value={value.employment_type || "full_time"} onChange={set("employment_type")}>
          {Object.entries(EMPLOYMENT_TYPES).map(([k, v]) => (
            <MenuItem key={k} value={k}>
              {v}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          {...tf}
          type="number"
          label="Annual CTC"
          value={value.ctc_annual ?? ""}
          onChange={set("ctc_annual")}
          InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
        />
        <TextField
          {...tf}
          select
          label="HRMS access"
          value={value.role || "employee"}
          onChange={set("role")}
          disabled={!me.isAdmin}
          helperText={me.isAdmin ? "Managers need nothing extra." : "Only HRMS admins can change this."}
        >
          {Object.entries(ROLE_LABELS).map(([k, v]) => (
            <MenuItem key={k} value={k}>
              {v}
            </MenuItem>
          ))}
        </TextField>
      </div>
      <div className={styles.sectionTitle}>Personal & contact</div>
      <div className={styles.formRow}>
        {PERSONAL_FIELDS.map(field)}
        <TextField {...tf} multiline label="Address" value={value.address ?? ""} onChange={set("address")} className={styles.span2} />
      </div>
      <TextField {...tf} multiline minRows={2} label="HR notes (never shown to the employee)" value={value.notes ?? ""} onChange={set("notes")} />
    </>
  );
}

const EDITABLE = [...JOB_FIELDS, ...PERSONAL_FIELDS].map(([k]) => k).concat(["manager_id", "employment_type", "ctc_annual", "role", "address", "notes"]);
const pickEditable = (e) => Object.fromEntries(EDITABLE.map((k) => [k, e[k] ?? ""]));

// ------------------------------------------------------------------ list

function AddEmployee({ me, employees, onAdded, onCancel }) {
  const [form, setForm] = useState({ employment_type: "full_time", role: "employee" });
  const { busy, run } = useAction();
  return (
    <Card
      title="Add employee"
      icon={<PersonAddAlt1OutlinedIcon />}
      subtitle="They start in onboarding with the onboarding checklist. Self-service works once they sign in to the portal with the work email."
      actions={
        <Button startIcon={<ArrowBackRoundedIcon />} onClick={onCancel} sx={{ ...btn, color: "#64748b" }}>
          Back
        </Button>
      }
    >
      <EmployeeForm value={form} onChange={setForm} me={me} employees={employees} />
      <div className={styles.actions} style={{ marginTop: 16, justifyContent: "flex-end" }}>
        <Button onClick={onCancel} sx={{ ...btn, color: "#64748b" }}>
          Cancel
        </Button>
        <Button
          className={styles.primaryBtn}
          variant="contained"
          disabled={busy || !form.first_name || !form.work_email}
          onClick={() => run("/employees", { method: "POST", body: form }, (d) => onAdded(d.employee.id))}
        >
          Add employee
        </Button>
      </div>
    </Card>
  );
}

const STATUS_FILTERS = [
  { value: "", label: "Current" },
  { value: "active", label: "Active" },
  { value: "onboarding", label: "Onboarding" },
  { value: "notice", label: "Serving notice" },
  { value: "exited", label: "Exited" },
  { value: "all", label: "All" },
];

export function Employees({ me }) {
  const [params, setParams] = useSearchParams();
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [query, setQuery] = useState("");
  const [openId, setOpenId] = useState(null);
  const adding = params.get("new") === "1";
  const setAdding = (on) => setParams(on ? { tab: "employees", new: "1" } : { tab: "employees" });
  const all = useLoad("/employees?status=all");
  const list = useLoad(`/employees?status=${status}&q=${encodeURIComponent(query)}`);
  const current = (all.data?.employees || []).filter((e) => e.status !== "exited");

  useEffect(() => {
    const t = setTimeout(() => setQuery(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  const back = () => {
    setOpenId(null);
    list.reload();
    all.reload();
  };

  if (openId) return <EmployeeDetail key={openId} id={openId} me={me} employees={current} onBack={back} />;
  if (adding) {
    return (
      <AddEmployee
        me={me}
        employees={current}
        onCancel={() => setAdding(false)}
        onAdded={(id) => {
          setAdding(false);
          all.reload();
          setOpenId(id);
        }}
      />
    );
  }
  const counts = {};
  for (const e of all.data?.employees || []) counts[e.status] = (counts[e.status] || 0) + 1;
  counts[""] = current.length;
  counts.all = (all.data?.employees || []).length;
  return (
    <Card
      title="Employees"
      icon={<GroupsOutlinedIcon />}
      actions={
        <Button className={styles.primaryBtn} variant="contained" startIcon={<PersonAddAlt1OutlinedIcon />} onClick={() => setAdding(true)}>
          Add employee
        </Button>
      }
    >
      <div className={styles.toolbar}>
        <Segmented options={STATUS_FILTERS.map((f) => ({ ...f, count: all.data ? counts[f.value] || 0 : undefined }))} value={status} onChange={setStatus} />
        <TextField
          size="small"
          placeholder="Search name, email or ID"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          sx={{ minWidth: 260 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRoundedIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </div>
      {!list.data ? (
        <Loading error={list.error} />
      ) : list.data.employees.length === 0 ? (
        <Empty icon={<GroupsOutlinedIcon />} title={query || status ? "No matches" : "No employees yet"} text={query || status ? "Try another filter." : "Add the first one."} />
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Employee</th>
                <th>ID</th>
                <th>Designation</th>
                <th>Reports to</th>
                <th>Joined</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {list.data.employees.map((e) => (
                <tr key={e.id} className={styles.clickRow} onClick={() => setOpenId(e.id)}>
                  <td>
                    <Person name={fullName(e)} sub={e.work_email} />
                  </td>
                  <td className={styles.mono}>{e.employee_code}</td>
                  <td>
                    {e.designation || "—"}
                    {e.role !== "employee" && (
                      <>
                        {" "}
                        <Tag color={e.role === "admin" ? "violet" : "teal"}>{ROLE_LABELS[e.role]}</Tag>
                      </>
                    )}
                  </td>
                  <td className={styles.small}>{e.manager ? e.manager.name : "—"}</td>
                  <td className={styles.small}>{fmtDate(e.joining_date)}</td>
                  <td>
                    <div className={styles.inline}>
                      <Pill status={e.status} />
                      {!e.portal_linked && e.status !== "exited" && <Tag color="amber">No login yet</Tag>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------- detail

function Checklist({ items, kind, employeeId, onChanged }) {
  const [title, setTitle] = useState("");
  const { busy, run } = useAction();
  const mine = items.filter((i) => i.kind === kind);
  const done = mine.filter((i) => i.done).length;
  return (
    <Card
      title={kind === "onboarding" ? "Onboarding" : "Offboarding"}
      icon={<ChecklistRtlOutlinedIcon />}
      color={kind === "onboarding" ? "green" : "amber"}
      actions={<Tag color={done === mine.length ? "green" : "amber"}>{`${done} / ${mine.length} done`}</Tag>}
    >
      <div className={styles.progress} style={{ marginTop: 0, marginBottom: 10 }}>
        <span style={{ width: `${mine.length ? (done / mine.length) * 100 : 0}%` }} />
      </div>
      {mine.map((i) => (
        <div key={i.id}>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={i.done}
                color="success"
                onChange={(e) => run(`/employees/${employeeId}/checklist/${i.id}`, { method: "PUT", body: { done: e.target.checked } }, onChanged)}
              />
            }
            label={
              <span style={{ fontSize: 14, textDecoration: i.done ? "line-through" : "none", color: i.done ? "#94a3b8" : "#0f172a" }}>
                {i.title}
                {i.done && i.done_by ? (
                  <span className={styles.small} style={{ textDecoration: "none" }}>
                    {" "}
                    · {i.done_by.split("@")[0]}, {fmtDate(i.done_at)}
                  </span>
                ) : null}
              </span>
            }
          />
        </div>
      ))}
      <div className={styles.inline} style={{ marginTop: 8, flexWrap: "nowrap" }}>
        <TextField size="small" placeholder="Add an item" value={title} onChange={(e) => setTitle(e.target.value)} fullWidth />
        <Button
          variant="outlined"
          disabled={busy || !title.trim()}
          onClick={() =>
            run(`/employees/${employeeId}/checklist`, { method: "POST", body: { kind, title } }, () => {
              setTitle("");
              onChanged();
            })
          }
          sx={btn}
        >
          Add
        </Button>
      </div>
    </Card>
  );
}

function Documents({ employeeId, documents, onChanged }) {
  const [file, setFile] = useState(null);
  const [meta, setMeta] = useState({ category: "payslip", period: "", title: "", visible: true });
  const [deleting, setDeleting] = useState(null);
  const { busy, run } = useAction();
  const upload = () => {
    const form = new FormData();
    form.append("file", file);
    form.append("category", meta.category);
    if (meta.category === "payslip") form.append("period", meta.period);
    if (meta.title) form.append("title", meta.title);
    form.append("visible_to_employee", meta.visible ? "true" : "false");
    run(`/employees/${employeeId}/documents`, { method: "POST", form }, () => {
      setFile(null);
      setMeta((m) => ({ ...m, title: "" }));
      onChanged();
    });
  };
  return (
    <>
      <Card title="Upload a payslip or document" icon={<UploadFileOutlinedIcon />} color="green">
        <div className={styles.formRow}>
          <TextField {...tf} select label="Type" value={meta.category} onChange={(e) => setMeta({ ...meta, category: e.target.value })}>
            {Object.entries(DOC_CATEGORIES).map(([k, v]) => (
              <MenuItem key={k} value={k}>
                {v}
              </MenuItem>
            ))}
          </TextField>
          {meta.category === "payslip" ? (
            <TextField {...tf} type="month" label="Payslip month" InputLabelProps={{ shrink: true }} value={meta.period} onChange={(e) => setMeta({ ...meta, period: e.target.value })} />
          ) : (
            <TextField {...tf} label="Title" value={meta.title} onChange={(e) => setMeta({ ...meta, title: e.target.value })} />
          )}
          <label className={styles.fileBtn}>
            <UploadFileOutlinedIcon fontSize="small" />
            {file ? file.name : "Choose file · PDF, image, Word · 10 MB"}
            <input type="file" hidden accept=".pdf,.png,.jpg,.jpeg,.doc,.docx" onChange={(e) => setFile(e.target.files[0] || null)} />
          </label>
        </div>
        <div className={styles.toolbar} style={{ marginBottom: 0 }}>
          <FormControlLabel
            control={<Checkbox size="small" checked={meta.visible} onChange={(e) => setMeta({ ...meta, visible: e.target.checked })} />}
            label={<span style={{ fontSize: 13.5 }}>Employee can see and download it</span>}
          />
          <Button className={styles.primaryBtn} variant="contained" disabled={busy || !file || (meta.category === "payslip" && !meta.period)} onClick={upload}>
            Upload
          </Button>
        </div>
      </Card>
      <Card title="Files" icon={<ReceiptLongOutlinedIcon />} color="violet">
        {documents.length === 0 ? (
          <Empty icon={<ReceiptLongOutlinedIcon />} text="No documents yet." />
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Document</th>
                  <th>Type</th>
                  <th>Uploaded</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {documents.map((d) => (
                  <tr key={d.id}>
                    <td>
                      <strong>{d.title}</strong> {!d.visible_to_employee && <Tag color="slate">HR only</Tag>}
                    </td>
                    <td>
                      <Tag color={d.category === "payslip" ? "green" : "violet"}>{DOC_CATEGORIES[d.category] || d.category}</Tag>
                    </td>
                    <td className={styles.small}>
                      {fmtDate(d.createdAt)} · {d.uploaded_by ? d.uploaded_by.split("@")[0] : ""}
                    </td>
                    <td className={styles.rowActions}>
                      <Button size="small" href={fileUrl(`/documents/${d.id}/download`)} startIcon={<FileDownloadOutlinedIcon />} sx={btn}>
                        Download
                      </Button>
                      <Button size="small" color="error" startIcon={<DeleteOutlineRoundedIcon />} onClick={() => setDeleting(d)} sx={btn}>
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      <ConfirmDialog
        open={Boolean(deleting)}
        title="Delete this document?"
        confirmLabel="Delete"
        danger
        onClose={() => setDeleting(null)}
        onConfirm={() => {
          const d = deleting;
          setDeleting(null);
          run(`/documents/${d.id}`, { method: "DELETE" }, onChanged);
        }}
      >
        {deleting && <p>“{deleting.title}” is removed from the server. This can't be undone.</p>}
      </ConfirmDialog>
    </>
  );
}

function LeaveSection({ employeeId, data, onChanged }) {
  const [adj, setAdj] = useState({ leave_type_id: "", days: "", reason: "" });
  const { busy, run } = useAction();
  return (
    <>
      <Card title={`Leave balance · ${data.year}`} icon={<BeachAccessOutlinedIcon />}>
        <Balances balances={data.balances} />
      </Card>
      <Card title="Adjust a balance" icon={<BeachAccessOutlinedIcon />} color="amber" subtitle="Carry-forward, encashment or a correction. Use a negative number to deduct.">
        <div className={styles.formRow}>
          <TextField {...tf} select label="Leave type" value={adj.leave_type_id} onChange={(e) => setAdj({ ...adj, leave_type_id: e.target.value })}>
            {data.balances
              .filter((b) => !b.unlimited)
              .map((b) => (
                <MenuItem key={b.leave_type_id} value={b.leave_type_id}>
                  {b.name}
                </MenuItem>
              ))}
          </TextField>
          <TextField {...tf} type="number" label="Days (±, steps of 0.5)" inputProps={{ step: 0.5 }} value={adj.days} onChange={(e) => setAdj({ ...adj, days: e.target.value })} />
          <TextField {...tf} label="Reason" value={adj.reason} onChange={(e) => setAdj({ ...adj, reason: e.target.value })} />
        </div>
        <Button
          className={styles.primaryBtn}
          variant="contained"
          disabled={busy || !adj.leave_type_id || !adj.days || !adj.reason.trim()}
          onClick={() =>
            run(`/employees/${employeeId}/leave-adjustments`, { method: "POST", body: { ...adj, year: data.year } }, () => {
              setAdj({ leave_type_id: "", days: "", reason: "" });
              onChanged();
            })
          }
        >
          Save adjustment
        </Button>
        {data.adjustments.length > 0 && (
          <div className={styles.tableWrap} style={{ marginTop: 14 }}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>When</th>
                  <th>Type</th>
                  <th>Days</th>
                  <th>Reason</th>
                  <th>By</th>
                </tr>
              </thead>
              <tbody>
                {data.adjustments.map((a) => (
                  <tr key={a.id}>
                    <td className={styles.small}>{fmtDate(a.createdAt)}</td>
                    <td>{(data.balances.find((b) => b.leave_type_id === a.leave_type_id) || {}).code}</td>
                    <td className={Number(a.days) > 0 ? styles.num_good : styles.num_bad}>{Number(a.days) > 0 ? `+${Number(a.days)}` : Number(a.days)}</td>
                    <td className={styles.small}>{a.reason}</td>
                    <td className={styles.small}>{a.created_by}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}

function AttendanceSection({ employeeId, me }) {
  const [month, setMonth] = useState(thisMonth(me.today));
  const { data, error, reload } = useLoad(`/employees/${employeeId}/attendance?month=${month}`);
  const [form, setForm] = useState({ date: "", check_in: me.settings.work_start, check_out: me.settings.work_end, note: "" });
  const { busy, run } = useAction();
  return (
    <>
      {data && <AttendanceTiles s={data.summary} />}
      <Card
        title="Attendance"
        icon={<TodayOutlinedIcon />}
        subtitle={data ? `${data.summary.working_days} working days` : undefined}
        actions={<MonthNav month={month} onChange={setMonth} />}
      >
        {!data ? (
          <Loading error={error} />
        ) : (
          <>
            <MonthCalendar month={month} cells={attendanceCells(data.days, me.settings.timezone)} today={me.today} />
            <DayLegend />
          </>
        )}
      </Card>
      <Card title="Set a day's attendance" icon={<TodayOutlinedIcon />} color="amber" subtitle="Overrides what was recorded for that day.">
        <div className={styles.formRow}>
          <TextField {...tf} type="date" label="Date" InputLabelProps={{ shrink: true }} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
          <TextField {...tf} type="time" label="Check in" InputLabelProps={{ shrink: true }} value={form.check_in} onChange={(e) => setForm({ ...form, check_in: e.target.value })} />
          <TextField {...tf} type="time" label="Check out" InputLabelProps={{ shrink: true }} value={form.check_out} onChange={(e) => setForm({ ...form, check_out: e.target.value })} />
          <TextField {...tf} label="Note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
        </div>
        <div className={styles.actions}>
          <Button className={styles.primaryBtn} variant="contained" disabled={busy || !form.date} onClick={() => run(`/employees/${employeeId}/attendance/${form.date}`, { method: "PUT", body: form }, reload)}>
            Save day
          </Button>
          <Button color="error" disabled={busy || !form.date} onClick={() => run(`/employees/${employeeId}/attendance/${form.date}`, { method: "PUT", body: { clear: true } }, reload)} sx={btn}>
            Clear day
          </Button>
        </div>
      </Card>
    </>
  );
}

function EmployeeDetail({ id, me, employees, onBack }) {
  const { data, error, reload } = useLoad(`/employees/${id}`);
  const [form, setForm] = useState(null);
  const [section, setSection] = useState("profile");
  const [dialog, setDialog] = useState(null); // offboard | exit | disable
  const [exit, setExit] = useState({ exit_date: "", exit_reason: "" });
  const { busy, run } = useAction();

  useEffect(() => {
    if (data) setForm(pickEditable(data.employee));
  }, [data]);

  if (!data || !form) return <Loading error={error} />;
  const e = data.employee;
  const name = fullName(e);
  const openOffboarding = data.checklist.filter((i) => i.kind === "offboarding" && !i.done).length;
  const checklistOpen = data.checklist.filter((i) => !i.done).length;

  return (
    <>
      <div className={styles.card}>
        <button type="button" className={styles.linkBtn} onClick={onBack} style={{ marginBottom: 12 }}>
          <ArrowBackRoundedIcon sx={{ fontSize: 17 }} /> All employees
        </button>
        <div className={styles.toolbar} style={{ marginBottom: 0 }}>
          <div className={styles.person} style={{ gap: 16 }}>
            <Avatar name={name} size={64} />
            <div style={{ minWidth: 0 }}>
              <div className={styles.inline}>
                <span style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.01em" }}>{name}</span>
                <Pill status={e.status} />
                {e.role !== "employee" && <Tag color={e.role === "admin" ? "violet" : "teal"}>{ROLE_LABELS[e.role]}</Tag>}
              </div>
              <div className={styles.muted} style={{ marginTop: 2 }}>
                {e.designation || "No designation"} · <span className={styles.mono}>{e.employee_code}</span>
                {e.manager_id && employees.find((x) => x.id === e.manager_id) ? ` · Reports to ${fullName(employees.find((x) => x.id === e.manager_id))}` : ""}
              </div>
              <div className={styles.inline} style={{ marginTop: 6 }}>
                {data.portalUser ? <Tag color="green">Portal login linked</Tag> : <Tag color="amber">No portal login yet</Tag>}
                {data.directReports.length > 0 && <Tag color="blue">{`Manages ${data.directReports.length}`}</Tag>}
                {e.exit_date && <Tag color="red">{`Last day ${fmtDate(e.exit_date)}`}</Tag>}
                {e.ctc_annual != null && <Tag color="slate">{`CTC ${inr(e.ctc_annual)}/yr`}</Tag>}
              </div>
            </div>
          </div>
          <div className={styles.actions}>
            {e.status === "onboarding" && (
              <Button variant="contained" color="success" disabled={busy} onClick={() => run(`/employees/${id}/activate`, { method: "POST" }, reload)} sx={btn}>
                Mark active
              </Button>
            )}
            {["onboarding", "active"].includes(e.status) && (
              <Button variant="outlined" color="warning" onClick={() => setDialog("offboard")} sx={btn}>
                Start offboarding
              </Button>
            )}
            {e.status === "notice" && (
              <Button variant="contained" color="error" onClick={() => setDialog("exit")} sx={btn}>
                Complete exit
              </Button>
            )}
            {me.portalSuper && data.portalUser && data.portalUser.status !== "inactive" && ["notice", "exited"].includes(e.status) && (
              <Button color="error" onClick={() => setDialog("disable")} sx={btn}>
                Disable portal login
              </Button>
            )}
          </div>
        </div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <Segmented
          value={section}
          onChange={setSection}
          options={[
            { value: "profile", label: "Profile", icon: <PersonOutlineIcon /> },
            { value: "documents", label: "Payslips & documents", icon: <ReceiptLongOutlinedIcon />, count: data.documents.length },
            { value: "leave", label: "Leave", icon: <BeachAccessOutlinedIcon /> },
            { value: "attendance", label: "Attendance", icon: <TodayOutlinedIcon /> },
            { value: "checklist", label: "Onboarding / exit", icon: <ChecklistRtlOutlinedIcon />, badge: checklistOpen || null },
          ]}
        />
      </div>

      {section === "profile" && (
        <Card title="Profile" icon={<BadgeOutlinedIcon />}>
          <EmployeeForm value={form} onChange={setForm} me={me} employees={employees} selfId={e.id} />
          <div className={styles.actions} style={{ marginTop: 16, justifyContent: "flex-end" }}>
            <Button className={styles.primaryBtn} variant="contained" disabled={busy} onClick={() => run(`/employees/${id}`, { method: "PUT", body: form }, reload)}>
              Save changes
            </Button>
          </div>
        </Card>
      )}
      {section === "documents" && <Documents employeeId={id} documents={data.documents} onChanged={reload} />}
      {section === "leave" && <LeaveSection employeeId={id} data={data} onChanged={reload} />}
      {section === "attendance" && <AttendanceSection employeeId={id} me={me} />}
      {section === "checklist" && (
        <div className={styles.grid2}>
          {data.checklist.some((i) => i.kind === "onboarding") && <Checklist items={data.checklist} kind="onboarding" employeeId={id} onChanged={reload} />}
          {data.checklist.some((i) => i.kind === "offboarding") && <Checklist items={data.checklist} kind="offboarding" employeeId={id} onChanged={reload} />}
          {!data.checklist.length && (
            <Card>
              <Empty icon={<ChecklistRtlOutlinedIcon />} text="No checklist items." />
            </Card>
          )}
          {e.exit_reason && (
            <Card title="Exit reason" color="red">
              <div className={styles.muted}>{e.exit_reason}</div>
            </Card>
          )}
        </div>
      )}

      <ConfirmDialog
        open={dialog === "offboard"}
        title={`Start offboarding ${name}?`}
        confirmLabel="Start offboarding"
        onClose={() => setDialog(null)}
        onConfirm={() =>
          run(`/employees/${id}/offboard`, { method: "POST", body: exit }, () => {
            setDialog(null);
            reload();
          })
        }
      >
        <p className={styles.muted}>Status becomes “Serving notice” and the offboarding checklist is added.</p>
        <div className={styles.formRow}>
          <TextField {...tf} type="date" label="Last working day" InputLabelProps={{ shrink: true }} value={exit.exit_date} onChange={(ev) => setExit({ ...exit, exit_date: ev.target.value })} />
        </div>
        <TextField {...tf} multiline label="Reason (resigned, terminated, contract end…)" value={exit.exit_reason} onChange={(ev) => setExit({ ...exit, exit_reason: ev.target.value })} />
      </ConfirmDialog>
      <ConfirmDialog
        open={dialog === "exit"}
        title={`Complete ${name}'s exit?`}
        confirmLabel={openOffboarding ? "Exit anyway" : "Complete exit"}
        danger
        onClose={() => setDialog(null)}
        onConfirm={() =>
          run(`/employees/${id}/complete-exit`, { method: "POST", body: { force: openOffboarding > 0 } }, () => {
            setDialog(null);
            reload();
          })
        }
      >
        <p>They move to “Exited”, stop appearing in current headcount, and their pending requests are cancelled.</p>
        {openOffboarding > 0 && <div className={styles.warnBox}>{openOffboarding} offboarding item(s) are still open.</div>}
        {me.portalSuper && <p className={styles.small}>Their portal login is not touched — use “Disable portal login” for that.</p>}
      </ConfirmDialog>
      <ConfirmDialog
        open={dialog === "disable"}
        title="Disable their portal login?"
        confirmLabel="Disable login"
        danger
        onClose={() => setDialog(null)}
        onConfirm={() =>
          run(`/employees/${id}/disable-login`, { method: "POST" }, () => {
            setDialog(null);
            reload();
          })
        }
      >
        <p>{data.portalUser && data.portalUser.email} can no longer sign in to the admin portal (same as disabling the user).</p>
      </ConfirmDialog>
    </>
  );
}
